"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type ComicKind,
  type ComicLabels,
  comicIsReady,
  COMIC_FIELDS as F,
  formatComic,
} from "@/lib/tool-kit/media-sources/comic-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const COMIC_CITATION_GENERATOR_STRINGS = {
  formHeading: "Comic details",
  resultsHeading: "Your citations",
  emptyPromptBook:
    "Enter the title of the graphic novel to see your citations.",
  emptyPromptIssue: "Enter the series title to see your citations.",
  emptyPromptWeb:
    "Enter the name of the webcomic or strip to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  kindLabel: "What are you citing?",
  kindBook: "Graphic novel or collected edition",
  kindIssue: "A single comic book issue",
  kindWeb: "Webcomic or comic strip online",
  writersLabel: "Writer(s)",
  writersPlaceholder: "John Lewis",
  writersHint:
    "One per line. If one person both writes and draws the comic, enter them here and as the artist.",
  creatorsLabel: "Creator(s)",
  creatorsPlaceholder: "Randall Munroe",
  artistsLabel: "Artist(s) or illustrator(s)",
  artistsPlaceholder: "Nate Powell",
  artistsHint: "One per line.",
  coloristLabel: "Colorist (optional)",
  coloristPlaceholder: "Dave McCaig",
  coloristHint:
    "MLA and Chicago can credit a colorist or letterer you discuss.",
  lettererLabel: "Letterer (optional)",
  lettererPlaceholder: "Fonografiks",
  bookTitleLabel: "Title",
  bookTitlePlaceholder: "March",
  seriesTitleLabel: "Series title",
  seriesTitlePlaceholder: "Saga",
  titleHint:
    "Type it in sentence case, as APA wants: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  volumeLabel: "Volume or book number (optional)",
  volumePlaceholder: "Book 1",
  volumeHint: "A number becomes vol. 2; a label such as Book 1 is kept.",
  issueLabel: "Issue number",
  issuePlaceholder: "1",
  publisherLabel: "Publisher",
  publisherPlaceholderBook: "Top Shelf Productions",
  publisherPlaceholderIssue: "Image Comics",
  dateLabelBook: "Year published",
  dateLabelIssue: "Cover date",
  dateLabelWeb: "Date posted",
  datePlaceholderBook: "2013",
  datePlaceholderIssue: "YYYY-MM",
  datePlaceholderWeb: "YYYY-MM-DD",
  stripTitleLabel: "Title of this comic or strip (optional)",
  stripTitlePlaceholder: "Exploits of a mom",
  webTitleLabel: "Name of the webcomic or strip",
  webTitlePlaceholder: "xkcd",
  webTitleHint: "As the comic writes it — xkcd stays lowercase.",
  stripKindLabel: "Kind",
  stripKindWebcomic: "Webcomic",
  stripKindStrip: "Newspaper comic strip read online",
  siteLabel: "Website, if it is not the comic's own (optional)",
  sitePlaceholder: "GoComics",
  urlLabel: "URL",
  urlPlaceholderBook: "https://… (only if you read it online)",
  urlPlaceholderWeb: "https://xkcd.com/327/",
  equalCreditLabel:
    "Credit the writer and the artist equally (MLA starts with the title)",
  equalCreditHint:
    "MLA's form for a collaboration when you are not focusing on one creator.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 has no comics example, so this follows its book format and the way it credits the illustrator of an illustrated book (Illus.), with a bracketed description of the format.",
  mlaNote:
    "MLA 9 names each contributor's role: illustrated by, colored by, lettered by. Use the equal-credit option to start with the title when writer and artist share the credit.",
  chicagoNote:
    "Chicago 18 cites a graphic novel or issue like a book, with the illustrator after the title. Titles of comic strips and webcomics are italic.",
  exampleBook: {
    writers: "John Lewis\nAndrew Aydin",
    artists: "Nate Powell",
    title: "March",
    volume: "Book 1",
    publisher: "Top Shelf Productions",
    date: "2013",
  },
  exampleIssue: {
    writers: "Brian K. Vaughan",
    artists: "Fiona Staples",
    title: "Saga",
    issue: "1",
    publisher: "Image Comics",
    date: "2012-03",
  },
  exampleWeb: {
    writers: "Randall Munroe",
    stripTitle: "Exploits of a mom",
    title: "xkcd",
    stripKind: "webcomic",
    date: "2007-10-10",
    url: "https://xkcd.com/327/",
  },
} as const;

type Strings = typeof COMIC_CITATION_GENERATOR_STRINGS;

function creditFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.writers,
      kind: "people",
      label: strings.writersLabel,
      placeholder: strings.writersPlaceholder,
      hint: strings.writersHint,
    },
    {
      id: F.artists,
      kind: "people",
      label: strings.artistsLabel,
      placeholder: strings.artistsPlaceholder,
      hint: strings.artistsHint,
    },
    {
      id: F.colorist,
      kind: "text",
      label: strings.coloristLabel,
      placeholder: strings.coloristPlaceholder,
      hint: strings.coloristHint,
    },
    {
      id: F.letterer,
      kind: "text",
      label: strings.lettererLabel,
      placeholder: strings.lettererPlaceholder,
    },
  ];
}

function printFields(strings: Strings, kind: "book" | "issue"): SourceField[] {
  const isBook = kind === "book";
  const fields: SourceField[] = [
    ...creditFields(strings),
    {
      id: F.title,
      kind: "text",
      label: isBook ? strings.bookTitleLabel : strings.seriesTitleLabel,
      placeholder: isBook
        ? strings.bookTitlePlaceholder
        : strings.seriesTitlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.volume,
      kind: "text",
      label: strings.volumeLabel,
      placeholder: strings.volumePlaceholder,
      hint: strings.volumeHint,
    },
  ];
  if (!isBook) {
    fields.push({
      id: F.issue,
      kind: "text",
      label: strings.issueLabel,
      placeholder: strings.issuePlaceholder,
    });
  }
  fields.push(
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: isBook
        ? strings.publisherPlaceholderBook
        : strings.publisherPlaceholderIssue,
    },
    {
      id: F.date,
      kind: "date",
      label: isBook ? strings.dateLabelBook : strings.dateLabelIssue,
      placeholder: isBook
        ? strings.datePlaceholderBook
        : strings.datePlaceholderIssue,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholderBook,
    },
    {
      id: F.equalCredit,
      kind: "checkbox",
      label: strings.equalCreditLabel,
      hint: strings.equalCreditHint,
      wide: true,
    }
  );
  return fields;
}

function webFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.writers,
      kind: "people",
      label: strings.creatorsLabel,
      placeholder: strings.creatorsPlaceholder,
    },
    {
      id: F.stripTitle,
      kind: "text",
      label: strings.stripTitleLabel,
      placeholder: strings.stripTitlePlaceholder,
      hint: strings.titleHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.webTitleLabel,
      placeholder: strings.webTitlePlaceholder,
      hint: strings.webTitleHint,
    },
    {
      id: F.stripKind,
      kind: "select",
      label: strings.stripKindLabel,
      options: [
        { value: "webcomic", label: strings.stripKindWebcomic },
        { value: "strip", label: strings.stripKindStrip },
      ],
    },
    {
      id: F.site,
      kind: "text",
      label: strings.siteLabel,
      placeholder: strings.sitePlaceholder,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabelWeb,
      placeholder: strings.datePlaceholderWeb,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholderWeb,
      wide: true,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function comicLabels(strings: Strings): ComicLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

const EMPTY_PROMPT_KEYS = {
  book: "emptyPromptBook",
  issue: "emptyPromptIssue",
  web: "emptyPromptWeb",
} as const;

const EXAMPLE_KEYS = {
  book: "exampleBook",
  issue: "exampleIssue",
  web: "exampleWeb",
} as const;

export default function ComicCitationGeneratorWidget() {
  const strings = useToolUi(COMIC_CITATION_GENERATOR_STRINGS);
  const [kind, setKind] = useState<ComicKind>("book");
  const fields = useMemo(
    () => (kind === "web" ? webFields(strings) : printFields(strings, kind)),
    [kind, strings]
  );
  const labels = useMemo(() => comicLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatComic(kind, values, labels),
    [kind, labels]
  );
  const builderStrings = useMemo(
    () => ({ ...strings, emptyPrompt: strings[EMPTY_PROMPT_KEYS[kind]] }),
    [kind, strings]
  );

  return (
    <SourceCitationBuilder
      example={strings[EXAMPLE_KEYS[kind]]}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.kindLabel}
          name="comic-kind"
          onChange={setKind}
          options={[
            { value: "book", label: strings.kindBook },
            { value: "issue", label: strings.kindIssue },
            { value: "web", label: strings.kindWeb },
          ]}
          value={kind}
        />
      }
      isReady={comicIsReady}
      strings={builderStrings}
    />
  );
}
